// src/pages/AdminCredits.tsx
//
// Admin console for the credits pricing system. Three sections:
//   1. Base price per credit (global)
//   2. Bundles — dynamic package catalog (add/edit/toggle/delete)
//   3. Promo codes — scoped or global, percent or fixed, with
//      redemption limits and validity windows
//
// All state is owned locally and refreshed after each mutation to keep
// the UI in lockstep with the DB — no optimistic updates here because
// credit pricing is low-frequency and we'd rather show the authoritative
// row than let the form drift.

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../utils/apiBase';
import {
  adminGrantCredits,
  fetchAdminPricing,
  updateAdminPricing,
  fetchAdminBundles,
  createAdminBundle,
  updateAdminBundle,
  deleteAdminBundle,
  fetchAdminPromoCodes,
  createAdminPromoCode,
  updateAdminPromoCode,
  deleteAdminPromoCode,
  type CreditBundle,
  type PromoCode,
} from '../utils/creditsApi';

const fmtUsd = (n: number) => `$${n.toFixed(2)}`;

/** Empty draft for the "add promo code" form. */
const BLANK_PROMO: Omit<PromoCode, 'id' | 'uses' | 'createdAt' | 'updatedAt'> = {
  code: '',
  description: '',
  discountType: 'percent',
  discountValue: 10,
  bundleId: null,
  maxUses: null,
  validFrom: null,
  validUntil: null,
  isActive: true,
};

const AdminCredits: React.FC = () => {
  const { token, user } = useAuth();

  // Pricing
  const [basePrice, setBasePrice] = useState<string>('2.50');
  const [savedBasePrice, setSavedBasePrice] = useState<number>(2.5);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [pricingMessage, setPricingMessage] = useState<string | null>(null);

  // Bundles
  const [bundles, setBundles] = useState<CreditBundle[]>([]);
  const [newBundle, setNewBundle] = useState({
    name: '',
    credits: '25',
    discountPercent: '10',
    sortOrder: '0',
    isActive: true,
  });
  const [bundleError, setBundleError] = useState<string | null>(null);

  // Promo codes
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [newPromo, setNewPromo] = useState<typeof BLANK_PROMO>({ ...BLANK_PROMO });
  const [promoError, setPromoError] = useState<string | null>(null);

  // Grant credits to another user
  const [grantUserId, setGrantUserId] = useState('');
  const [grantCredits, setGrantCredits] = useState('');
  const [grantNote, setGrantNote] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantResult, setGrantResult] = useState<string | null>(null);

  // User search for grant
  const [userSearch, setUserSearch] = useState('');
  const [allUsers, setAllUsers] = useState<Array<{ id: number; name: string; email: string; role: string }>>([]);
  const [filteredUsers, setFilteredUsers] = useState<Array<{ id: number; name: string; email: string; role: string }>>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedUserLabel, setSelectedUserLabel] = useState('');

  const [loading, setLoading] = useState(true);

  const reload = async () => {
    if (!token) return;
    const [pricing, bs, ps] = await Promise.all([
      fetchAdminPricing(token),
      fetchAdminBundles(token),
      fetchAdminPromoCodes(token),
    ]);
    setBasePrice(pricing.basePricePerCredit.toFixed(2));
    setSavedBasePrice(pricing.basePricePerCredit);
    setBundles(bs);
    setPromoCodes(ps);
  };

  // Load users for the grant picker
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/admin/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => { if (data.users) setAllUsers(data.users); })
      .catch(() => {});
  }, [token]);

  // Filter users as the search input changes
  useEffect(() => {
    if (!userSearch.trim()) { setFilteredUsers([]); return; }
    const q = userSearch.toLowerCase();
    setFilteredUsers(
      allUsers.filter((u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        String(u.id) === q
      ).slice(0, 10)
    );
  }, [userSearch, allUsers]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await reload();
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // -----------------------------------------------------------------
  // Pricing handlers
  // -----------------------------------------------------------------

  const handleSavePricing = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    const value = Number(basePrice);
    if (!Number.isFinite(value) || value < 0) {
      setPricingMessage('Price must be a non-negative number.');
      return;
    }
    setPricingSaving(true);
    setPricingMessage(null);
    const ok = await updateAdminPricing(token, value);
    if (ok) {
      setSavedBasePrice(value);
      setPricingMessage('Saved. Existing bundles will re-price automatically.');
      // Refresh bundles so their computed prices reflect the new base.
      const bs = await fetchAdminBundles(token);
      setBundles(bs);
    } else {
      setPricingMessage('Failed to save. Please try again.');
    }
    setPricingSaving(false);
  };

  // -----------------------------------------------------------------
  // Bundle handlers
  // -----------------------------------------------------------------

  const handleCreateBundle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setBundleError(null);
    const credits = parseInt(newBundle.credits, 10);
    const discount = Number(newBundle.discountPercent);
    if (!newBundle.name.trim()) return setBundleError('Name is required.');
    if (!Number.isFinite(credits) || credits <= 0) return setBundleError('Credits must be a positive integer.');
    if (!Number.isFinite(discount) || discount < 0 || discount > 100) return setBundleError('Discount must be 0–100.');

    const created = await createAdminBundle(token, {
      name: newBundle.name.trim(),
      credits,
      discountPercent: discount,
      isActive: newBundle.isActive,
      sortOrder: parseInt(newBundle.sortOrder, 10) || 0,
    });
    if (created) {
      setNewBundle({ name: '', credits: '25', discountPercent: '10', sortOrder: '0', isActive: true });
      await reload();
    } else {
      setBundleError('Failed to create bundle.');
    }
  };

  const handleInlineBundleChange = async (
    id: number,
    patch: Partial<{ name: string; credits: number; discountPercent: number; isActive: boolean; sortOrder: number }>,
  ) => {
    if (!token) return;
    // Optimistic local update so the form feels snappy even when the
    // round-trip takes a moment; reload on failure to snap back.
    setBundles((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
    const updated = await updateAdminBundle(token, id, patch);
    if (!updated) {
      await reload();
    } else {
      // Price fields (subtotal/total/pricePerCredit) are recomputed by
      // the backend on read, not returned by PUT — reload so they stay
      // accurate after a credits/discount change.
      const fresh = await fetchAdminBundles(token);
      setBundles(fresh);
    }
  };

  const handleDeleteBundle = async (b: CreditBundle) => {
    if (!token) return;
    if (!window.confirm(`Delete "${b.name}"? This cannot be undone.`)) return;
    const ok = await deleteAdminBundle(token, b.id);
    if (ok) await reload();
  };

  // -----------------------------------------------------------------
  // Promo code handlers
  // -----------------------------------------------------------------

  const handleCreatePromo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setPromoError(null);
    if (!newPromo.code.trim()) return setPromoError('Code is required.');
    if (!Number.isFinite(Number(newPromo.discountValue)) || Number(newPromo.discountValue) < 0) {
      return setPromoError('Discount value must be non-negative.');
    }

    const created = await createAdminPromoCode(token, {
      code: newPromo.code.trim().toUpperCase(),
      description: newPromo.description || null,
      discountType: newPromo.discountType,
      discountValue: Number(newPromo.discountValue),
      bundleId: newPromo.bundleId,
      maxUses: newPromo.maxUses,
      validFrom: newPromo.validFrom,
      validUntil: newPromo.validUntil,
      isActive: newPromo.isActive,
    });
    if (created) {
      setNewPromo({ ...BLANK_PROMO });
      await reload();
    } else {
      setPromoError('Failed to create promo code. Check that the code is unique.');
    }
  };

  const handleInlinePromoChange = async (id: number, patch: Partial<PromoCode>) => {
    if (!token) return;
    setPromoCodes((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    const updated = await updateAdminPromoCode(token, id, patch);
    if (!updated) {
      await reload();
    }
  };

  const handleDeletePromo = async (p: PromoCode) => {
    if (!token) return;
    if (!window.confirm(`Delete promo code "${p.code}"? This cannot be undone.`)) return;
    const ok = await deleteAdminPromoCode(token, p.id);
    if (ok) await reload();
  };

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------

  if (loading) {
    return (
      <div className="section container" style={{ textAlign: 'center', padding: '4rem' }}>
        <p>Loading credits config...</p>
      </div>
    );
  }

  return (
    <div className="section container">
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div>
          <h1 style={{ marginBottom: '0.25rem' }}>Credits Management</h1>
          <p className="text-secondary">Signed in as {user?.email}</p>
        </div>

        {/* ------------------------------------------------ */}
        {/* Section 1 — Base price per credit                */}
        {/* ------------------------------------------------ */}
        <div style={{ marginTop: '2rem' }}>
          <h2 style={{ marginBottom: '1rem' }}>Base Pricing</h2>
          <div className="card" style={{ padding: '1.5rem' }}>
            <form onSubmit={handleSavePricing} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '0 1 180px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Price per Credit (USD)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={basePrice}
                  onChange={(e) => setBasePrice(e.target.value)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
                <p className="text-secondary" style={{ margin: '0.35rem 0 0', fontSize: '0.75rem' }}>
                  Currently saved: {fmtUsd(savedBasePrice)}
                </p>
              </div>
              <button type="submit" className="btn btn-primary" disabled={pricingSaving}>
                {pricingSaving ? 'Saving…' : 'Save Pricing'}
              </button>
              {pricingMessage && (
                <p style={{ margin: 0, fontSize: '0.85rem', color: pricingMessage.includes('Failed') ? '#dc2626' : '#166534' }}>
                  {pricingMessage}
                </p>
              )}
            </form>
            <p className="text-secondary" style={{ fontSize: '0.8rem', marginTop: '0.75rem' }}>
              This is the retail price charged per credit before any bundle discount or promo code is applied.
              Bundles multiply this number by their credit count and subtract their discount percent.
            </p>
          </div>
        </div>

        {/* ------------------------------------------------ */}
        {/* Section 2 — Bundles                              */}
        {/* ------------------------------------------------ */}
        <div style={{ marginTop: '2.5rem' }}>
          <h2 style={{ marginBottom: '1rem' }}>Credit Bundles</h2>

          <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.05rem' }}>Add Bundle</h3>
            <form onSubmit={handleCreateBundle} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 180px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Name</label>
                <input
                  type="text"
                  value={newBundle.name}
                  onChange={(e) => setNewBundle({ ...newBundle, name: e.target.value })}
                  placeholder="e.g. 100 Credits"
                  required
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ flex: '0 1 110px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Credits</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={newBundle.credits}
                  onChange={(e) => setNewBundle({ ...newBundle, credits: e.target.value })}
                  required
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ flex: '0 1 110px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Discount %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={newBundle.discountPercent}
                  onChange={(e) => setNewBundle({ ...newBundle, discountPercent: e.target.value })}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ flex: '0 1 100px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Sort</label>
                <input
                  type="number"
                  step="1"
                  value={newBundle.sortOrder}
                  onChange={(e) => setNewBundle({ ...newBundle, sortOrder: e.target.value })}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={newBundle.isActive}
                  onChange={(e) => setNewBundle({ ...newBundle, isActive: e.target.checked })}
                />
                Active
              </label>
              <button type="submit" className="btn btn-primary">Add</button>
            </form>
            {bundleError && <p style={{ color: '#dc2626', marginTop: '0.75rem', fontSize: '0.875rem' }}>{bundleError}</p>}
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Name</th>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Credits</th>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Discount %</th>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Sale Price</th>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Sort</th>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Active</th>
                  <th style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #e5e7eb' }} />
                </tr>
              </thead>
              <tbody>
                {bundles.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>
                      No bundles yet. Add one above.
                    </td>
                  </tr>
                )}
                {bundles.map((b) => (
                  <tr key={b.id}>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6' }}>
                      <input
                        type="text"
                        value={b.name}
                        onChange={(e) => setBundles((prev) => prev.map((x) => (x.id === b.id ? { ...x, name: e.target.value } : x)))}
                        onBlur={() => handleInlineBundleChange(b.id, { name: b.name })}
                        style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                      />
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                      <input
                        type="number"
                        min="1"
                        value={b.credits}
                        onChange={(e) => setBundles((prev) => prev.map((x) => (x.id === b.id ? { ...x, credits: parseInt(e.target.value, 10) || 0 } : x)))}
                        onBlur={() => handleInlineBundleChange(b.id, { credits: b.credits })}
                        style={{ width: '80px', padding: '0.4rem 0.5rem', border: '1px solid #e5e7eb', borderRadius: '6px', textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={b.discountPercent}
                        onChange={(e) => setBundles((prev) => prev.map((x) => (x.id === b.id ? { ...x, discountPercent: Number(e.target.value) } : x)))}
                        onBlur={() => handleInlineBundleChange(b.id, { discountPercent: b.discountPercent })}
                        style={{ width: '80px', padding: '0.4rem 0.5rem', border: '1px solid #e5e7eb', borderRadius: '6px', textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                      {b.total != null ? fmtUsd(b.total) : fmtUsd(b.credits * savedBasePrice * (1 - b.discountPercent / 100))}
                      {b.total != null && b.discountPercent > 0 && b.subtotal != null && (
                        <span style={{ display: 'block', fontSize: '0.75rem', color: '#9ca3af', textDecoration: 'line-through' }}>
                          {fmtUsd(b.subtotal)}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                      <input
                        type="number"
                        value={b.sortOrder}
                        onChange={(e) => setBundles((prev) => prev.map((x) => (x.id === b.id ? { ...x, sortOrder: parseInt(e.target.value, 10) || 0 } : x)))}
                        onBlur={() => handleInlineBundleChange(b.id, { sortOrder: b.sortOrder })}
                        style={{ width: '70px', padding: '0.4rem 0.5rem', border: '1px solid #e5e7eb', borderRadius: '6px', textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={b.isActive}
                        onChange={(e) => handleInlineBundleChange(b.id, { isActive: e.target.checked })}
                      />
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                      <button
                        onClick={() => handleDeleteBundle(b)}
                        style={{
                          padding: '0.4rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #fca5a5',
                          backgroundColor: '#fff',
                          color: '#dc2626',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-secondary" style={{ fontSize: '0.8rem', marginTop: '0.75rem' }}>
            Inactive bundles stay in the database but disappear from the public /credits page.
            Edit a field and click anywhere else to save.
          </p>
        </div>

        {/* ------------------------------------------------ */}
        {/* Section 3 — Grant credits to a user              */}
        {/* ------------------------------------------------ */}
        <div style={{ marginTop: '2.5rem' }}>
          <h2 style={{ marginBottom: '1rem' }}>Grant Credits to User</h2>
          <div className="card" style={{ padding: '1.5rem' }}>
            <p className="text-secondary" style={{ fontSize: '0.8rem', marginBottom: '1rem' }}>
              Purchase credits on behalf of another user. The grant is recorded in their credit history
              with your admin ID for audit tracking.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 280px', position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 500, fontSize: '0.85rem' }}>User (search by name, email, or ID)</label>
                <input
                  type="text"
                  value={selectedUserLabel || userSearch}
                  onChange={(e) => {
                    setUserSearch(e.target.value);
                    setSelectedUserLabel('');
                    setGrantUserId('');
                    setShowUserDropdown(true);
                  }}
                  onFocus={() => { if (userSearch.trim()) setShowUserDropdown(true); }}
                  placeholder="Type a name, email, or user ID..."
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
                {showUserDropdown && filteredUsers.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '0 0 6px 6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto',
                  }}>
                    {filteredUsers.map((u) => (
                      <div
                        key={u.id}
                        onClick={() => {
                          setGrantUserId(String(u.id));
                          setSelectedUserLabel(`${u.name || u.email} (ID: ${u.id})`);
                          setUserSearch('');
                          setShowUserDropdown(false);
                        }}
                        style={{
                          padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem',
                          borderBottom: '1px solid #f3f4f6',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#fff'; }}
                      >
                        <div style={{ fontWeight: 500 }}>{u.name || '(no name)'}</div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {u.email} — ID: {u.id} — {u.role}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ flex: '0 1 140px' }}>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 500, fontSize: '0.85rem' }}>Credits</label>
                <input
                  type="number"
                  min="1"
                  value={grantCredits}
                  onChange={(e) => setGrantCredits(e.target.value)}
                  placeholder="500"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 500, fontSize: '0.85rem' }}>Note (optional)</label>
                <input
                  type="text"
                  value={grantNote}
                  onChange={(e) => setGrantNote(e.target.value)}
                  placeholder="Reason for grant"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <button
                className="btn btn-primary"
                disabled={grantLoading || !grantUserId || !grantCredits}
                onClick={async () => {
                  if (!token) return;
                  setGrantLoading(true);
                  setGrantResult(null);
                  const result = await adminGrantCredits(token, {
                    userId: parseInt(grantUserId, 10),
                    credits: parseInt(grantCredits, 10),
                    note: grantNote || undefined,
                  });
                  if (result?.success) {
                    setGrantResult(`Granted ${result.credited?.toLocaleString()} credits to ${result.user?.name || result.user?.email} — new balance: ${result.balance?.toLocaleString()}`);
                    setGrantUserId('');
                    setGrantCredits('');
                    setGrantNote('');
                    setSelectedUserLabel('');
                    setUserSearch('');
                  } else {
                    setGrantResult('Failed to grant credits. Check the user ID.');
                  }
                  setGrantLoading(false);
                }}
              >
                {grantLoading ? 'Granting…' : 'Grant Credits'}
              </button>
            </div>
            {grantResult && (
              <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: grantResult.includes('Failed') ? '#dc2626' : '#166534' }}>
                {grantResult}
              </p>
            )}
          </div>
        </div>

        {/* ------------------------------------------------ */}
        {/* Section 4 — Promo codes                          */}
        {/* ------------------------------------------------ */}
        <div style={{ marginTop: '2.5rem', marginBottom: '3rem' }}>
          <h2 style={{ marginBottom: '1rem' }}>Promo Codes</h2>

          <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.05rem' }}>Add Promo Code</h3>
            <form onSubmit={handleCreatePromo} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 500 }}>Code</label>
                <input
                  type="text"
                  value={newPromo.code}
                  onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value })}
                  placeholder="e.g. WELCOME10"
                  required
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', textTransform: 'uppercase' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 500 }}>Type</label>
                <select
                  value={newPromo.discountType}
                  onChange={(e) => setNewPromo({ ...newPromo, discountType: e.target.value as 'percent' | 'fixed' })}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                >
                  <option value="percent">Percent off</option>
                  <option value="fixed">Fixed amount</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 500 }}>
                  Value ({newPromo.discountType === 'percent' ? '%' : '$'})
                </label>
                <input
                  type="number"
                  min="0"
                  step={newPromo.discountType === 'percent' ? '0.5' : '0.01'}
                  value={newPromo.discountValue}
                  onChange={(e) => setNewPromo({ ...newPromo, discountValue: Number(e.target.value) })}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 500 }}>Applies to</label>
                <select
                  value={newPromo.bundleId == null ? '' : String(newPromo.bundleId)}
                  onChange={(e) => setNewPromo({ ...newPromo, bundleId: e.target.value ? Number(e.target.value) : null })}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                >
                  <option value="">All bundles</option>
                  {bundles.filter((b) => b.isActive).map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 500 }}>Max uses</label>
                <input
                  type="number"
                  min="0"
                  value={newPromo.maxUses ?? ''}
                  onChange={(e) => setNewPromo({ ...newPromo, maxUses: e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10) || 0) })}
                  placeholder="unlimited"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 500 }}>Valid from</label>
                <input
                  type="datetime-local"
                  value={newPromo.validFrom ?? ''}
                  onChange={(e) => setNewPromo({ ...newPromo, validFrom: e.target.value || null })}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 500 }}>Valid until</label>
                <input
                  type="datetime-local"
                  value={newPromo.validUntil ?? ''}
                  onChange={(e) => setNewPromo({ ...newPromo, validUntil: e.target.value || null })}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 500 }}>Description (optional)</label>
                <input
                  type="text"
                  value={newPromo.description ?? ''}
                  onChange={(e) => setNewPromo({ ...newPromo, description: e.target.value })}
                  placeholder="Shown to admins only"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '0.75rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                  <input
                    type="checkbox"
                    checked={newPromo.isActive}
                    onChange={(e) => setNewPromo({ ...newPromo, isActive: e.target.checked })}
                  />
                  Active
                </label>
                <button type="submit" className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>Add Code</button>
              </div>
            </form>
            {promoError && <p style={{ color: '#dc2626', marginTop: '0.75rem', fontSize: '0.875rem' }}>{promoError}</p>}
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Code</th>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Discount</th>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Applies To</th>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Uses</th>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Window</th>
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Active</th>
                  <th style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #e5e7eb' }} />
                </tr>
              </thead>
              <tbody>
                {promoCodes.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>
                      No promo codes yet.
                    </td>
                  </tr>
                )}
                {promoCodes.map((p) => {
                  const bundleName = p.bundleId == null ? 'All bundles' : (bundles.find((b) => b.id === p.bundleId)?.name ?? `#${p.bundleId}`);
                  const window = [
                    p.validFrom ? `From ${new Date(p.validFrom).toLocaleDateString()}` : null,
                    p.validUntil ? `Until ${new Date(p.validUntil).toLocaleDateString()}` : null,
                  ].filter(Boolean).join(' · ') || 'Always';
                  return (
                    <tr key={p.id}>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6', fontFamily: 'monospace', fontWeight: 600 }}>
                        {p.code}
                        {p.description && (
                          <div style={{ fontFamily: 'inherit', fontWeight: 400, fontSize: '0.75rem', color: '#6b7280' }}>
                            {p.description}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6' }}>
                        {p.discountType === 'percent'
                          ? `${p.discountValue}% off`
                          : `${fmtUsd(p.discountValue)} off`}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6' }}>
                        {bundleName}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                        {p.uses}{p.maxUses != null ? ` / ${p.maxUses}` : ''}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6', fontSize: '0.85rem', color: '#6b7280' }}>
                        {window}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={p.isActive}
                          onChange={(e) => handleInlinePromoChange(p.id, { isActive: e.target.checked })}
                        />
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                        <button
                          onClick={() => handleDeletePromo(p)}
                          style={{
                            padding: '0.4rem 0.75rem',
                            borderRadius: '6px',
                            border: '1px solid #fca5a5',
                            backgroundColor: '#fff',
                            color: '#dc2626',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-secondary" style={{ fontSize: '0.8rem', marginTop: '0.75rem' }}>
            Promo codes apply on top of bundle discounts. Use "Applies to → All bundles" to let a
            code work against any purchase; pick a specific bundle to scope it.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminCredits;
